import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { getDashboardStats, getRealAnalytics, getOutstandingDuesRemovedUsers } from '@/services/api';
import { Users, FileText, Calendar, CreditCard, CheckCircle2, Clock, XCircle, AlertCircle, IndianRupee, Home, UserX, TrendingUp, Shield, Building, Bell, CalendarDays, UserPlus } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const COLORS = ['#0EA5E9', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const AdminHome = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [outstandingData, setOutstandingData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [dashboardData, analyticsData, duesData] = await Promise.all([
        getDashboardStats(),
        getRealAnalytics(),
        getOutstandingDuesRemovedUsers().catch(() => null)
      ]);
      setStats(dashboardData);
      setAnalytics(analyticsData);
      setOutstandingData(duesData);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
      </div>
    );
  }

  const revenue = analytics?.revenue || {};
  const rent = analytics?.rent || {};
  const occupancy = analytics?.occupancy || {};

  // Revenue pie data
  const revenuePieData = [
    { name: 'Collected', value: revenue.collected || 0, color: '#10B981' },
    { name: 'Pending', value: revenue.pending || 0, color: '#F59E0B' },
    { name: 'Written Off', value: revenue.written_off || 0, color: '#9CA3AF' }
  ].filter(d => d.value > 0);

  // Rent pie data
  const rentPieData = [
    { name: 'Paid', value: rent.paid || 0, color: '#10B981' },
    { name: 'Unpaid', value: rent.unpaid || 0, color: '#EF4444' },
    { name: 'Submitted', value: rent.submitted || 0, color: '#3B82F6' },
    { name: 'Written Off', value: rent.written_off || 0, color: '#9CA3AF' }
  ].filter(d => d.value > 0);

  // Monthly rent trend
  const monthlyRent = (rent.monthly || []).map(m => ({
    ...m,
    month: m.month?.substring(5) || m.month
  }));

  // Complaint categories
  const complaintCategories = analytics?.complaints?.by_category ?
    Object.entries(analytics.complaints.by_category).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1), value
    })) : [];

  // Amenity usage
  const amenityUsage = analytics?.bookings?.by_amenity ?
    Object.entries(analytics.bookings.by_amenity).map(([name, bookings]) => ({
      name, bookings
    })) : [];

  // Occupancy data for bar chart
  const occupancyData = [
    { name: 'Residents', count: occupancy.residents || 0, fill: '#3B82F6' },
    { name: 'Tenants', count: occupancy.tenants || 0, fill: '#10B981' },
    { name: 'Security', count: occupancy.security || 0, fill: '#F59E0B' },
    { name: 'Pending', count: occupancy.pending_approvals || 0, fill: '#8B5CF6' },
    { name: 'Removed', count: occupancy.removed || 0, fill: '#EF4444' }
  ];

  const totalRevenue = (revenue.collected || 0) + (revenue.pending || 0) + (revenue.written_off || 0);
  const outstandingTotal = outstandingData?.summary?.total_outstanding || 0;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Admin Dashboard</h1>
        <p className="text-sm md:text-base text-slate-600">Society overview at a glance</p>
      </div>

      {/* Top Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/admin/residents')} data-testid="stat-residents">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Residents</p>
                <p className="text-xl font-bold text-slate-900">{stats?.total_residents || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/admin/occupancy')} data-testid="stat-tenants">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-green-50 rounded-xl flex items-center justify-center">
                <Building className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Tenants</p>
                <p className="text-xl font-bold text-slate-900">{occupancy.tenants || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm" data-testid="stat-complaints">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-orange-50 rounded-xl flex items-center justify-center">
                <FileText className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Open Issues</p>
                <p className="text-xl font-bold text-slate-900">{stats?.open_complaints || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm" data-testid="stat-bookings">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                <Calendar className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Bookings</p>
                <p className="text-xl font-bold text-slate-900">{stats?.total_bookings || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm cursor-pointer hover:shadow-md" onClick={() => navigate('/admin/bills')} data-testid="stat-pending-bills">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-red-50 rounded-xl flex items-center justify-center">
                <CreditCard className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Pending Bills</p>
                <p className="text-xl font-bold text-slate-900">{stats?.pending_bills || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm cursor-pointer hover:shadow-md" onClick={() => navigate('/admin/residents')} data-testid="stat-approvals">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-amber-50 rounded-xl flex items-center justify-center">
                <UserPlus className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Pending Approvals</p>
                <p className="text-xl font-bold text-slate-900">{stats?.pending_approvals || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm cursor-pointer hover:shadow-md" onClick={() => navigate('/admin/events')} data-testid="stat-events">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-purple-50 rounded-xl flex items-center justify-center">
                <CalendarDays className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Events</p>
                <p className="text-xl font-bold text-slate-900">{stats?.total_events || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm cursor-pointer hover:shadow-md" onClick={() => navigate('/admin/announcements')} data-testid="stat-announcements">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-indigo-50 rounded-xl flex items-center justify-center">
                <Bell className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Announcements</p>
                <p className="text-xl font-bold text-slate-900">{stats?.total_announcements || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {outstandingTotal > 0 && (
          <Card className="border-red-200 shadow-sm bg-red-50/50 cursor-pointer hover:shadow-md" onClick={() => navigate('/admin/bills')} data-testid="stat-outstanding">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-red-100 rounded-xl flex items-center justify-center">
                  <UserX className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xs text-red-600">Outstanding</p>
                  <p className="text-lg font-bold text-red-800 flex items-center gap-0.5">
                    <IndianRupee className="h-3.5 w-3.5" />{outstandingTotal.toLocaleString('en-IN')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Revenue & Rent Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Breakdown */}
        <Card className="border-slate-200 shadow-sm" data-testid="chart-revenue">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Revenue Overview</CardTitle>
            <CardDescription className="text-xs">Bills collection status</CardDescription>
          </CardHeader>
          <CardContent>
            {revenuePieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={revenuePieData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3} dataKey="value">
                    {revenuePieData.map((entry, i) => (<Cell key={i} fill={entry.color} />))}
                  </Pie>
                  <Tooltip formatter={(v) => `\u20b9${v.toLocaleString('en-IN')}`} />
                  <Legend iconSize={10} wrapperStyle={{ fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[200px] text-slate-400 text-sm">No revenue data</div>
            )}
            <div className="grid grid-cols-3 gap-2 mt-2">
              <div className="text-center p-2 bg-green-50 rounded-lg">
                <p className="text-xs text-slate-500">Collected</p>
                <p className="text-sm font-bold text-green-700">{'\u20b9'}{(revenue.collected || 0).toLocaleString('en-IN')}</p>
              </div>
              <div className="text-center p-2 bg-amber-50 rounded-lg">
                <p className="text-xs text-slate-500">Pending</p>
                <p className="text-sm font-bold text-amber-700">{'\u20b9'}{(revenue.pending || 0).toLocaleString('en-IN')}</p>
              </div>
              <div className="text-center p-2 bg-slate-100 rounded-lg">
                <p className="text-xs text-slate-500">Written Off</p>
                <p className="text-sm font-bold text-slate-600">{'\u20b9'}{(revenue.written_off || 0).toLocaleString('en-IN')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rent Collection */}
        <Card className="border-slate-200 shadow-sm" data-testid="chart-rent">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Rent Collection</CardTitle>
            <CardDescription className="text-xs">Tenant rent payment status</CardDescription>
          </CardHeader>
          <CardContent>
            {rentPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={rentPieData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3} dataKey="value">
                    {rentPieData.map((entry, i) => (<Cell key={i} fill={entry.color} />))}
                  </Pie>
                  <Tooltip formatter={(v) => `\u20b9${v.toLocaleString('en-IN')}`} />
                  <Legend iconSize={10} wrapperStyle={{ fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[200px] text-slate-400 text-sm">No rent data</div>
            )}
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div className="text-center p-2 bg-green-50 rounded-lg">
                <p className="text-xs text-slate-500">Paid</p>
                <p className="text-sm font-bold text-green-700">{'\u20b9'}{(rent.paid || 0).toLocaleString('en-IN')}</p>
              </div>
              <div className="text-center p-2 bg-red-50 rounded-lg">
                <p className="text-xs text-slate-500">Unpaid</p>
                <p className="text-sm font-bold text-red-700">{'\u20b9'}{(rent.unpaid || 0).toLocaleString('en-IN')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Occupancy Overview */}
        <Card className="border-slate-200 shadow-sm" data-testid="chart-occupancy">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Occupancy Overview</CardTitle>
            <CardDescription className="text-xs">Current society composition</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={occupancyData} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={65} />
                <Tooltip />
                <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                  {occupancyData.map((entry, i) => (<Cell key={i} fill={entry.fill} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="flex items-center justify-between text-xs text-slate-500 mt-2 px-1">
              <span>Active Leases: <strong className="text-slate-700">{occupancy.active_leases || 0}</strong></span>
              <span>Terminated: <strong className="text-slate-700">{occupancy.terminated_leases || 0}</strong></span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Rent Trend */}
        {monthlyRent.length > 0 && (
          <Card className="border-slate-200 shadow-sm" data-testid="chart-rent-trend">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Monthly Rent Trend</CardTitle>
              <CardDescription className="text-xs">Paid vs unpaid rent by month</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={monthlyRent}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => `\u20b9${v.toLocaleString('en-IN')}`} />
                  <Legend iconSize={10} wrapperStyle={{ fontSize: '12px' }} />
                  <Area type="monotone" dataKey="paid" stackId="1" stroke="#10B981" fill="#10B981" fillOpacity={0.5} name="Paid" />
                  <Area type="monotone" dataKey="unpaid" stackId="1" stroke="#EF4444" fill="#EF4444" fillOpacity={0.3} name="Unpaid" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Amenity Usage */}
        <Card className="border-slate-200 shadow-sm" data-testid="chart-amenity-usage">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Amenity Usage</CardTitle>
            <CardDescription className="text-xs">Booking count by amenity</CardDescription>
          </CardHeader>
          <CardContent>
            {amenityUsage.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={amenityUsage}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="bookings" radius={[6, 6, 0, 0]}>
                    {amenityUsage.map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[240px] text-slate-400 text-sm">No booking data</div>
            )}
          </CardContent>
        </Card>

        {/* Complaint Distribution */}
        <Card className="border-slate-200 shadow-sm" data-testid="chart-complaints">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Complaint Distribution</CardTitle>
            <CardDescription className="text-xs">Issues by category</CardDescription>
          </CardHeader>
          <CardContent>
            {complaintCategories.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={complaintCategories} cx="50%" cy="50%" labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80} dataKey="value">
                    {complaintCategories.map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[240px] text-slate-400 text-sm">No complaint data</div>
            )}
            <div className="grid grid-cols-3 gap-2 mt-2 text-center">
              <div className="p-2 bg-orange-50 rounded-lg">
                <p className="text-xs text-slate-500">Open</p>
                <p className="text-sm font-bold text-orange-700">{analytics?.complaints?.open || 0}</p>
              </div>
              <div className="p-2 bg-green-50 rounded-lg">
                <p className="text-xs text-slate-500">Resolved</p>
                <p className="text-sm font-bold text-green-700">{analytics?.complaints?.resolved || 0}</p>
              </div>
              <div className="p-2 bg-slate-100 rounded-lg">
                <p className="text-xs text-slate-500">Total</p>
                <p className="text-sm font-bold text-slate-700">{analytics?.complaints?.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Booking Status */}
        <Card className="border-slate-200 shadow-sm" data-testid="chart-booking-status">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Booking Status</CardTitle>
            <CardDescription className="text-xs">Current booking breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-4 bg-blue-50 rounded-xl flex items-center gap-3">
                <Clock className="h-7 w-7 text-blue-600" />
                <div>
                  <p className="text-xs text-slate-500">Confirmed</p>
                  <p className="text-xl font-bold text-slate-900">{analytics?.bookings?.by_status?.confirmed || 0}</p>
                </div>
              </div>
              <div className="p-4 bg-green-50 rounded-xl flex items-center gap-3">
                <CheckCircle2 className="h-7 w-7 text-green-600" />
                <div>
                  <p className="text-xs text-slate-500">Used</p>
                  <p className="text-xl font-bold text-slate-900">{analytics?.bookings?.by_status?.used || 0}</p>
                </div>
              </div>
              <div className="p-4 bg-slate-100 rounded-xl flex items-center gap-3">
                <AlertCircle className="h-7 w-7 text-slate-500" />
                <div>
                  <p className="text-xs text-slate-500">Expired</p>
                  <p className="text-xl font-bold text-slate-900">{analytics?.bookings?.by_status?.expired || 0}</p>
                </div>
              </div>
              <div className="p-4 bg-red-50 rounded-xl flex items-center gap-3">
                <XCircle className="h-7 w-7 text-red-600" />
                <div>
                  <p className="text-xs text-slate-500">Cancelled</p>
                  <p className="text-xl font-bold text-slate-900">{analytics?.bookings?.by_status?.cancelled || 0}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/admin/residents')}>
          <CardContent className="p-4">
            <Users className="h-5 w-5 text-blue-600 mb-2" />
            <p className="text-sm font-semibold text-slate-900">Personnel</p>
            <p className="text-xs text-slate-500">Manage users</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/admin/announcements')}>
          <CardContent className="p-4">
            <FileText className="h-5 w-5 text-green-600 mb-2" />
            <p className="text-sm font-semibold text-slate-900">Announcements</p>
            <p className="text-xs text-slate-500">Post updates</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/admin/bills')}>
          <CardContent className="p-4">
            <IndianRupee className="h-5 w-5 text-amber-600 mb-2" />
            <p className="text-sm font-semibold text-slate-900">Bills</p>
            <p className="text-xs text-slate-500">Manage billing</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/admin/removal-history')}>
          <CardContent className="p-4">
            <UserX className="h-5 w-5 text-red-600 mb-2" />
            <p className="text-sm font-semibold text-slate-900">Removals</p>
            <p className="text-xs text-slate-500">View history</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminHome;
